import { useState, useRef, useEffect } from 'react';
import { Play, RotateCcw, Send, X, Save, FolderOpen, ChevronDown, ChevronUp, Bug } from 'lucide-react';
import { API_URL } from '../config';
import { useAuth0 } from '@auth0/auth0-react';
import { isAdminUser } from '../auth/auth0-config';

interface Message {
  role: 'bot' | 'user';
  content: string;
  timestamp: Date;
}

interface DebugLog {
  timestamp: Date;
  type: 'request' | 'response' | 'error' | 'info';
  title: string;
  data: any;
}

interface ChatSimulatorProps {
  flowDefinition: any;
  flowName: string;
  onClose: () => void;
  onOpenHistory?: () => void;
  loadedMessages?: Message[];
  isEmpty?: boolean;
}

export default function ChatSimulator({ flowDefinition, flowName, onClose, onOpenHistory, loadedMessages, isEmpty = false }: ChatSimulatorProps) {
  const [messages, setMessages] = useState<Message[]>(loadedMessages || []);
  const [inputValue, setInputValue] = useState('');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [waitingForInput, setWaitingForInput] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [quickReplies, setQuickReplies] = useState<string[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // Debug panel (solo admin)
  const { user } = useAuth0();
  const isAdmin = user ? isAdminUser(user) : false;
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [debugLogs, setDebugLogs] = useState<DebugLog[]>([]);
  const debugEndRef = useRef<HTMLDivElement>(null);
  
  const addDebugLog = (type: DebugLog['type'], title: string, data: any) => {
    if (!isAdmin) return;
    setDebugLogs(prev => [...prev, {
      timestamp: new Date(),
      type,
      title,
      data
    }]);
  };
  
  const clearDebugLogs = () => setDebugLogs([]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Carica messaggi salvati se disponibili
  useEffect(() => {
    if (loadedMessages && loadedMessages.length > 0) {
      setMessages(loadedMessages);
      setIsCompleted(true); // Chat caricata è già completata
    }
  }, [loadedMessages]);

  const saveChat = () => {
    if (messages.length === 0) {
      alert('❌ Nessun messaggio da salvare!');
      return;
    }

    const chatName = prompt('💾 Nome per questa conversazione:', `Chat ${new Date().toLocaleString('it-IT')}`);
    if (!chatName) return;

    const saved = localStorage.getItem('pigna-flow-chat-history');
    const chats = saved ? JSON.parse(saved) : [];
    
    const newChat = {
      id: `chat-${Date.now()}`,
      name: chatName.trim(),
      flow_name: flowName,
      timestamp: new Date().toISOString(),
      messages: messages.map(m => ({
        ...m,
        timestamp: m.timestamp.toISOString()
      })),
      session_id: sessionId || 'unknown'
    };

    chats.push(newChat);
    localStorage.setItem('pigna-flow-chat-history', JSON.stringify(chats));
    alert(`✅ Conversazione "${chatName}" salvata!`);
  };

  // Handler per quick reply buttons
  const handleQuickReply = async (value: string) => {
    setInputValue(value);
    await sendMessage(value);
    setQuickReplies([]); // Reset quick replies dopo l'uso
  };

  const startConversation = async () => {
    // Controllo canvas vuota
    if (isEmpty) {
      setMessages([{
        role: 'bot',
        content: '❌ Nessun flow da eseguire! Crea almeno un nodo START sulla canvas.',
        timestamp: new Date(),
      }]);
      return;
    }

    setMessages([]);
    setIsCompleted(false);
    setWaitingForInput(false);
    setIsLoading(true);
    
    // 🔥 FORZA NUOVA SESSIONE: Genera sempre un nuovo session_id per evitare cache
    const newSessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setSessionId(newSessionId);
    
    console.log('🚀 Avvio nuova conversazione:', {
      sessionId: newSessionId,
      nodes: flowDefinition.nodes?.length || 0,
      edges: flowDefinition.edges?.length || 0,
    });

    try {
      const response = await fetch(`${API_URL}/flow/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          flow_definition: flowDefinition,
          session_id: null, // Lascia null per forzare il backend a crearne uno nuovo
          user_input: null,
        }),
      });

      if (!response.ok) {
        // Prova a estrarre il messaggio di errore dal backend
        try {
          const errorData = await response.json();
          throw new Error(errorData.detail || `HTTP ${response.status}`);
        } catch (e) {
          throw new Error(`HTTP ${response.status}`);
        }
      }

      const data = await response.json();
      setSessionId(data.session_id);

      // Aggiungi messaggio bot
      if (data.content) {
        setMessages([{
          role: 'bot',
          content: data.content,
          timestamp: new Date(),
        }]);
      }

      // Check se attende input
      if (data.type === 'input_required') {
        setWaitingForInput(true);
        // Estrai quick_replies se disponibili
        if (data.quick_replies && Array.isArray(data.quick_replies)) {
          setQuickReplies(data.quick_replies);
        } else {
          setQuickReplies([]);
        }
      } else if (data.type === 'completed') {
        setIsCompleted(true);
        setQuickReplies([]);
      }
    } catch (error: any) {
      console.error('Errore avvio conversazione:', error);
      
      // Prova a estrarre il messaggio di errore dal backend
      let errorMessage = 'Errore sconosciuto';
      if (error.message) {
        errorMessage = error.message;
      }
      
      setMessages([{
        role: 'bot',
        content: `❌ Errore: ${errorMessage}. 
        
🔍 Possibili cause:
• Il flow non ha un nodo START
• Il backend non è avviato ({API_URL})
• Il flow ha errori di struttura`,
        timestamp: new Date(),
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async (quickReplyValue?: string) => {
    const messageToSend = quickReplyValue || inputValue.trim();
    if (!messageToSend || !sessionId || isLoading) return;

    const userMessage = messageToSend;
    setInputValue('');
    setIsLoading(true);
    setQuickReplies([]); // Reset quick replies

    // Aggiungi messaggio utente
    setMessages(prev => [...prev, {
      role: 'user',
      content: userMessage,
      timestamp: new Date(),
    }]);

    setWaitingForInput(false);

    try {
      const requestBody = {
        flow_definition: flowDefinition,
        session_id: sessionId,
        user_input: userMessage,
      };
      
      addDebugLog('request', '📤 API Request', {
        url: `${API_URL}/flow/execute`,
        user_input: userMessage,
        session_id: sessionId
      });
      
      const response = await fetch(`${API_URL}/flow/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        addDebugLog('error', '❌ HTTP Error', { status: response.status });
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      
      addDebugLog('response', '📥 API Response', {
        type: data.type,
        content: data.content?.substring(0, 200) + (data.content?.length > 200 ? '...' : ''),
        next_node: data.next_node,
        debug: data.debug, // Info debug dal backend
        variables: data.state?.variables
      });

      // Aggiungi risposta bot SOLO se content non è vuoto
      // Quando IntentLLM trova un intento, content è vuoto e non deve essere mostrato
      if (data.content && data.content.trim() !== '') {
        setMessages(prev => [...prev, {
          role: 'bot',
          content: data.content,
          timestamp: new Date(),
        }]);
      }

      // Check stato
      if (data.type === 'input_required') {
        setWaitingForInput(true);
        // Estrai quick_replies se disponibili
        if (data.quick_replies && Array.isArray(data.quick_replies)) {
          setQuickReplies(data.quick_replies);
        } else {
          setQuickReplies([]);
        }
      } else if (data.type === 'completed') {
        setIsCompleted(true);
        setWaitingForInput(false);
        setQuickReplies([]);
      } else if (data.type === 'message' && data.next_node) {
        // Se è un messaggio con next_node, processa automaticamente il nodo successivo
        // Questo permette a llm_qualifier di passare immediatamente al nodo successivo quando decide "Yes"
        console.log(`🔄 Frontend: type=message con next_node=${data.next_node}, chiamo processNextNode`);
        await processNextNode(data.next_node, data.session_id);
      } else {
        // 🔥 DEBUG: Log quando NON chiamiamo processNextNode
        console.log(`⏹️ Frontend: type=${data.type}, next_node=${data.next_node} - NON chiamo processNextNode`);
      }
    } catch (error) {
      console.error('Errore invio messaggio:', error);
      addDebugLog('error', '❌ Exception', { error: String(error) });
      setMessages(prev => [...prev, {
        role: 'bot',
        content: `❌ Errore: ${error}`,
        timestamp: new Date(),
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  // Funzione ricorsiva per processare automaticamente i nodi successivi
  const processNextNode = async (_nextNodeId: string, sessionId: string, depth: number = 0): Promise<void> => {
    // Protezione contro loop infiniti (max 10 nodi in sequenza)
    if (depth > 10) {
      console.warn('⚠️ Troppi nodi processati in sequenza, fermo per evitare loop');
      addDebugLog('error', '⚠️ Loop Protection', { depth, message: 'Troppi nodi in sequenza' });
      return;
    }

    console.log(`🔄 Frontend: Processo automaticamente nodo successivo (depth: ${depth})`);
    addDebugLog('info', `🔄 Auto-process node (depth: ${depth})`, { next_node: _nextNodeId });

    try {
      const response = await fetch(`${API_URL}/flow/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          flow_definition: flowDefinition,
          session_id: sessionId,
          user_input: null, // Nessun input utente, solo processamento automatico
        }),
      });

      if (!response.ok) {
        addDebugLog('error', '❌ HTTP Error (auto)', { status: response.status });
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      
      addDebugLog('response', '📥 Auto Response', {
        type: data.type,
        content: data.content?.substring(0, 200) + (data.content?.length > 200 ? '...' : ''),
        next_node: data.next_node,
        debug: data.debug
      });

      // Aggiungi risposta bot SOLO se content non è vuoto
      // Quando IntentLLM trova un intento, content è vuoto e non deve essere mostrato
      if (data.content && data.content.trim() !== '') {
        setMessages(prev => [...prev, {
          role: 'bot',
          content: data.content,
          timestamp: new Date(),
        }]);
      }

      // Se richiede input o è completato, ferma la catena
      if (data.type === 'input_required') {
        setWaitingForInput(true);
        if (data.quick_replies && Array.isArray(data.quick_replies)) {
          setQuickReplies(data.quick_replies);
        } else {
          setQuickReplies([]);
        }
      } else if (data.type === 'completed') {
        setIsCompleted(true);
        setWaitingForInput(false);
        setQuickReplies([]);
      } else if (data.type === 'message' && data.next_node) {
        // Continua a processare ricorsivamente se c'è un altro next_node
        await processNextNode(data.next_node, data.session_id || sessionId, depth + 1);
      }
    } catch (error) {
      console.error('Errore processamento nodo successivo:', error);
      setMessages(prev => [...prev, {
        role: 'bot',
        content: `⚠️ Errore nel processamento automatico: ${error}`,
        timestamp: new Date(),
      }]);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="w-96 bg-white border-l-4 border-purple-300 flex flex-col shadow-2xl h-full">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-2xl">💬</span>
          <div>
            <h3 className="font-bold text-lg">Simulazione</h3>
            <p className="text-xs opacity-90">Test del flow in tempo reale</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Controls */}
      <div className="p-3 bg-gray-50 border-b space-y-2">
        <div className="flex gap-2">
          <button
            onClick={startConversation}
            disabled={isLoading || isEmpty}
            className="flex-1 bg-green-500 hover:bg-green-600 disabled:bg-gray-300 text-white px-3 py-2 rounded-lg font-semibold text-sm transition flex items-center justify-center gap-2"
            title={isEmpty ? 'Canvas vuota - aggiungi nodi prima!' : 'Avvia simulazione'}
          >
            <Play className="w-4 h-4" />
            {isEmpty ? '⚠️ Canvas vuota' : (sessionId ? 'Riavvia' : 'Play')}
          </button>
          <button
            onClick={() => {
              setMessages([]);
              setSessionId(null);
              setIsCompleted(false);
              setWaitingForInput(false);
              setQuickReplies([]);
            }}
            disabled={messages.length === 0}
            className="bg-gray-500 hover:bg-gray-600 disabled:bg-gray-300 text-white px-3 py-2 rounded-lg font-semibold text-sm transition flex items-center gap-2"
            title="Reset chat"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
        <div className="flex gap-2">
          <button
            onClick={saveChat}
            disabled={messages.length === 0}
            className="flex-1 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 text-white px-3 py-2 rounded-lg font-semibold text-sm transition flex items-center justify-center gap-2"
            title="Salva conversazione"
          >
            <Save className="w-4 h-4" />
            Salva Chat
          </button>
          {onOpenHistory && (
            <button
              onClick={onOpenHistory}
              className="bg-purple-500 hover:bg-purple-600 text-white px-3 py-2 rounded-lg font-semibold text-sm transition flex items-center gap-2"
              title="Visualizza cronologia"
            >
              <FolderOpen className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
        {messages.length === 0 && (
          <div className="text-center text-gray-500 mt-8">
            <div className="text-6xl mb-4">🚀</div>
            <p className="text-sm">Clicca "Play" per iniziare la simulazione</p>
            <p className="text-xs mt-2 text-gray-400">
              Il backend deve essere avviato su porta 8100
            </p>
          </div>
        )}

        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                msg.role === 'user'
                  ? 'bg-blue-500 text-white'
                  : 'bg-white border-2 border-gray-200 text-gray-800'
              }`}
            >
              <div className="text-sm whitespace-pre-wrap">{msg.content}</div>
              <div
                className={`text-xs mt-1 ${
                  msg.role === 'user' ? 'text-blue-100' : 'text-gray-400'
                }`}
              >
                {msg.timestamp.toLocaleTimeString()}
              </div>
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-white border-2 border-gray-200 rounded-2xl px-4 py-2">
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
            </div>
          </div>
        )}

        {/* Il nodo End non mostra messaggi - conversazione termina silenziosamente */}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-3 bg-white border-t-2 border-gray-200">
        {waitingForInput && !isCompleted ? (
          <div className="space-y-2">
            {/* Quick Reply Buttons */}
            {quickReplies.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {quickReplies.map((reply, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuickReply(reply)}
                    disabled={isLoading}
                    className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 disabled:from-gray-300 disabled:to-gray-300 text-white rounded-full text-sm font-semibold transition shadow-md hover:shadow-lg transform hover:scale-105"
                  >
                    {reply}
                  </button>
                ))}
              </div>
            )}
            
            {/* Text Input */}
            <div className="flex gap-2">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={quickReplies.length > 0 ? "Oppure scrivi..." : "Scrivi la tua risposta..."}
                className="flex-1 px-4 py-2 border-2 border-purple-300 rounded-lg focus:outline-none focus:border-purple-500"
                autoFocus={quickReplies.length === 0}
                disabled={isLoading}
              />
              <button
                onClick={() => sendMessage()}
                disabled={!inputValue.trim() || isLoading}
                className="bg-purple-500 hover:bg-purple-600 disabled:bg-gray-300 text-white px-4 py-2 rounded-lg transition"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center text-gray-400 text-sm py-2">
            {isCompleted ? 'Clicca "Play" per ricominciare' : 'Clicca "Play" per iniziare'}
          </div>
        )}
      </div>
      
      {/* 🐛 Debug Panel - Solo Admin */}
      {isAdmin && (
        <div className="border-t-2 border-orange-300">
          {/* Toggle Header */}
          <button
            onClick={() => setShowDebugPanel(!showDebugPanel)}
            className="w-full px-3 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white text-sm font-bold flex items-center justify-between hover:from-orange-600 hover:to-red-600 transition"
          >
            <div className="flex items-center gap-2">
              <Bug className="w-4 h-4" />
              <span>🔧 Debug Console (Admin)</span>
              <span className="bg-white text-orange-600 px-2 py-0.5 rounded-full text-xs">
                {debugLogs.length}
              </span>
            </div>
            {showDebugPanel ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
          </button>
          
          {/* Debug Content */}
          {showDebugPanel && (
            <div className="bg-gray-900 text-green-400 font-mono text-xs max-h-64 overflow-y-auto">
              {/* Controls */}
              <div className="sticky top-0 bg-gray-800 p-2 flex justify-between items-center border-b border-gray-700">
                <span className="text-gray-400">📋 Log API ({debugLogs.length} entries)</span>
                <button
                  onClick={clearDebugLogs}
                  className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs"
                >
                  Clear
                </button>
              </div>
              
              {/* Logs */}
              <div className="p-2 space-y-2">
                {debugLogs.length === 0 ? (
                  <div className="text-gray-500 text-center py-4">
                    Nessun log. Avvia una simulazione per vedere i dettagli.
                  </div>
                ) : (
                  debugLogs.map((log, idx) => (
                    <div 
                      key={idx} 
                      className={`p-2 rounded border-l-4 ${
                        log.type === 'request' ? 'border-blue-500 bg-blue-900/30' :
                        log.type === 'response' ? 'border-green-500 bg-green-900/30' :
                        log.type === 'error' ? 'border-red-500 bg-red-900/30' :
                        'border-yellow-500 bg-yellow-900/30'
                      }`}
                    >
                      <div className="flex justify-between items-center mb-1">
                        <span className={`font-bold ${
                          log.type === 'request' ? 'text-blue-400' :
                          log.type === 'response' ? 'text-green-400' :
                          log.type === 'error' ? 'text-red-400' :
                          'text-yellow-400'
                        }`}>
                          {log.title}
                        </span>
                        <span className="text-gray-500 text-[10px]">
                          {log.timestamp.toLocaleTimeString()}
                        </span>
                      </div>
                      <pre className="text-[10px] text-gray-300 overflow-x-auto whitespace-pre-wrap">
                        {JSON.stringify(log.data, null, 2)}
                      </pre>
                    </div>
                  ))
                )}
                <div ref={debugEndRef} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

